import { FormattedMessage, useIntl } from "react-intl";
import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
} from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";

import useReportError from "@aglocal/web/hooks/useReportError";

import useDarkquell from "@aglocal/web/firebase/queries/useDarkquell";
import useSkillClassifier from "@aglocal/web/firebase/queries/useSkillClassifier";

import Callout from "@aglocal/web/components/Callout";

import SkillPills from "@/components/SkillPills";
import getRecordingNotice from "@/helpers/getRecordingNotice";

export interface SkillsAccordionItemProps {
  name: unknown;
  interview: Interview;
}

export default function SkillsAccordionItem({
  name,
  interview,
}: SkillsAccordionItemProps) {
  const intl = useIntl();
  const [skills, , darkquellError] = useDarkquell(interview.id);
  const [negativeSkills, , skillClassifierError] = useSkillClassifier(
    interview.id,
  );
  const error = darkquellError ?? skillClassifierError;

  useReportError(darkquellError);
  useReportError(skillClassifierError);

  const notice = getRecordingNotice(intl, interview, "skills");

  return (
    <AccordionItem value={name}>
      <AccordionHeader>
        <FormattedMessage
          defaultMessage="Skills"
          description="Header of the in-meeting sidebar section listing the skills detected in the conversation."
        />
      </AccordionHeader>
      <AccordionPanel>
        {error && (
          <Callout
            intent="error"
            title={intl.$t({
              defaultMessage: "Error loading skills",
              description:
                "Error shown when the skills detected in the conversation could not be loaded.",
            })}
          />
        )}
        {skills.length === 0 ? (
          notice ? (
            <Callout intent={notice.intent} title={notice.title}>
              {notice.body}
            </Callout>
          ) : (
            <Callout
              intent="warning"
              title={intl.$t({
                defaultMessage: "No skills found",
                description:
                  "Shown when no skills have been detected in the conversation yet.",
              })}
            />
          )
        ) : (
          <SkillPills skills={skills} negativeSkills={negativeSkills} />
        )}
      </AccordionPanel>
    </AccordionItem>
  );
}
